import { memo, useCallback, useState, useRef, useMemo, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { cn } from "@/shared/utils/general/cn"
import type { DbObject } from "@/db/schema"
import { ObjectType } from "@/db/schema"
import { updateTaskTags } from "@/db/operations"
import { useAllTags } from "../hooks/useAllTags"
import { getInheritedTags, useAncestorTagStore } from "@/features/tags"
import { useTagColors } from "@/features/tags/useTagColors"
import { scrollElementIntoView } from "./checklist/scrollUtils"
import CrossIcon from "@/shared/icons/totodoicons/stroke/cross.svg?react"

// Module-level style constants to avoid new object allocations in .map() loops
const DEFAULT_TAG_STYLE = {
  backgroundColor: "oklch(from var(--primary) 0.94 0.04 h)",
  color: "var(--primary)",
} as const
const DEFAULT_TAG_REMOVE_STYLE = {
  color: "oklch(from var(--primary) l c h / 0.3)",
} as const
const COLORED_TAG_REMOVE_STYLE = {
  color: "rgba(255,255,255,0.5)",
} as const

interface DetailTagsProps {
  task: DbObject
  /** When true, start in adding mode with focus */
  autoFocus?: boolean
  /** Called when the auto-focus adding session ends */
  onClose?: () => void
}

/**
 * Tag chips in expanded task detail — view + add/remove tags.
 *
 * Features:
 * - Removable chips for existing tags
 * - Autocomplete dropdown with all workspace tags
 * - Keyboard nav: ↑↓ to navigate, Enter to select, Escape to close
 * - Match highlighting in suggestions
 * - Pikaicons (cross × , tag icon)
 */
export const DetailTags = memo(function DetailTags({
  task,
  autoFocus,
  onClose,
}: DetailTagsProps) {
  const { t } = useTranslation()
  const id = task.id
  const tags = task.tags ?? []
  const [isAdding, setIsAdding] = useState(autoFocus ?? false)
  const [inputValue, setInputValue] = useState("")
  const [highlightIndex, setHighlightIndex] = useState(-1)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [allTags] = useAllTags()
  const { tagColors } = useTagColors()

  // Inherited tags from parent container/area (read-only, non-reactive via getState)
  const inheritedTags = useMemo(() => {
    // Only tasks inherit tags — containers are the source
    if (
      task.type === ObjectType.Project ||
      task.type === ObjectType.List ||
      task.type === ObjectType.Area
    )
      return []
    const parentPath = task.parentPath ?? ""
    if (!parentPath || parentPath === "root") return []
    const state = useAncestorTagStore.getState()
    const inherited = getInheritedTags(parentPath, state)
    // Exclude tags already directly on the task
    return inherited.filter((t) => !tags.includes(t))
  }, [task.type, task.parentPath, tags])

  // Auto-focus input when opened via quick-action
  useEffect(() => {
    if (autoFocus && isAdding) {
      requestAnimationFrame(() => {
        inputRef.current?.focus({ preventScroll: true })
        // Scroll tag section into view — it may be below the fold
        // when activated via quick-action icon on a tall card.
        scrollElementIntoView(containerRef.current)
      })
    }
  }, [autoFocus, isAdding])

  // Filtered suggestions: exclude already-applied tags, filter by input
  const suggestions = useMemo(() => {
    const available = allTags.filter((t) => !tags.includes(t))
    if (!inputValue.trim()) return available
    const query = inputValue.toLowerCase()
    return available.filter((t) => t.toLowerCase().includes(query))
  }, [allTags, tags, inputValue])

  // Reset highlight when suggestions change
  useEffect(() => {
    setHighlightIndex(-1)
  }, [suggestions])

  const handleRemoveTag = useCallback(
    async (tagToRemove: string) => {
      if (!id) return
      const updated = tags.filter((t) => t !== tagToRemove)
      await updateTaskTags(id, updated)
    },
    [id, tags],
  )

  const addTag = useCallback(
    async (tag: string) => {
      const trimmed = tag.trim()
      if (!trimmed || !id) return
      if (tags.includes(trimmed)) {
        setInputValue("")
        return
      }
      await updateTaskTags(id, [...tags, trimmed])
      setInputValue("")
      setHighlightIndex(-1)
      // Re-focus for consecutive adds
      requestAnimationFrame(() => inputRef.current?.focus({ preventScroll: true }))
    },
    [id, tags],
  )

  const commitTag = useCallback(() => {
    const trimmed = inputValue.trim()
    if (trimmed) {
      addTag(trimmed)
    }
    setInputValue("")
    setIsAdding(false)
    setHighlightIndex(-1)
    onClose?.()
  }, [inputValue, addTag, onClose])

  // Click outside to close
  useEffect(() => {
    if (!isAdding) return
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        commitTag()
      }
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [isAdding, commitTag])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault()
        setHighlightIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : 0,
        )
      } else if (e.key === "ArrowUp") {
        e.preventDefault()
        setHighlightIndex((prev) =>
          prev > 0 ? prev - 1 : suggestions.length - 1,
        )
      } else if (e.key === "Enter") {
        e.preventDefault()
        const highlighted = suggestions[highlightIndex]
        if (highlightIndex >= 0 && highlighted) {
          addTag(highlighted)
        } else if (inputValue.trim()) {
          addTag(inputValue)
        }
      } else if (e.key === "Escape") {
        e.preventDefault()
        setInputValue("")
        setIsAdding(false)
        setHighlightIndex(-1)
        onClose?.()
      } else if (e.key === "Backspace" && !inputValue) {
        // Backspace on empty input → remove last tag (Slack/Discord pattern)
        e.preventDefault()
        if (tags.length > 0) {
          handleRemoveTag(tags[tags.length - 1]!)
        }
      } else if (e.key === "Tab") {
        // Tab commits current input
        commitTag()
      }
    },
    [
      highlightIndex,
      suggestions,
      inputValue,
      addTag,
      commitTag,
      onClose,
      tags,
      handleRemoveTag,
    ],
  )

  const startAdding = useCallback(() => {
    setIsAdding(true)
    requestAnimationFrame(() => inputRef.current?.focus({ preventScroll: true }))
  }, [])

  // Highlight matching text in suggestion
  const highlightMatch = useCallback(
    (text: string) => {
      if (!inputValue.trim()) return text
      const query = inputValue.toLowerCase()
      const idx = text.toLowerCase().indexOf(query)
      if (idx === -1) return text
      return (
        <>
          {text.slice(0, idx)}
          <span className="font-semibold text-foreground">
            {text.slice(idx, idx + inputValue.length)}
          </span>
          {text.slice(idx + inputValue.length)}
        </>
      )
    },
    [inputValue],
  )

  return (
    <div ref={containerRef} className="relative px-4 pb-5 pt-1.5">
      <div className="flex flex-wrap items-center gap-1.5">
        {/* Existing tags — colored bg if set, else primary-light */}
        {tags.map((tag) => {
          const color = tagColors[tag]
          return (
            <span
              key={tag}
              className="chip-enter inline-flex h-6 items-center gap-1.5 rounded-full px-2.5"
              style={
                color
                  ? { backgroundColor: color, color: "#fff" }
                  : DEFAULT_TAG_STYLE
              }
            >
              <span className="max-w-[150px] truncate text-sm font-medium leading-none">
                {tag}
              </span>
              <button
                onClick={() => handleRemoveTag(tag)}
                className="flex shrink-0 items-center justify-center rounded-full transition-colors"
                style={
                  color ? COLORED_TAG_REMOVE_STYLE : DEFAULT_TAG_REMOVE_STYLE
                }
                aria-label={t("Remove tag {{tag}}", { tag })}
              >
                <CrossIcon className="size-4" />
              </button>
            </span>
          )
        })}

        {/* Inherited tags from parent container/area — dimmed, not removable */}
        {inheritedTags.map((tag) => (
          <span
            key={`inherited-${tag}`}
            className="inline-flex h-6 items-center gap-1.5 rounded-full px-2.5 opacity-40"
            style={DEFAULT_TAG_STYLE}
            title={t("Inherited from container")}
          >
            <span className="max-w-[150px] truncate text-sm font-medium leading-none">
              {tag}
            </span>
          </span>
        ))}

        {/* Add tag — autocomplete input */}
        {isAdding ? (
          <div className="relative">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value)
                setHighlightIndex(-1)
              }}
              onKeyDown={handleKeyDown}
              placeholder={t("Add Tag")}
              aria-label={t("Add Tag")}
              role="combobox"
              aria-expanded={suggestions.length > 0}
              aria-autocomplete="list"
              enterKeyHint="done"
              data-tag-input="true"
              className={cn(
                "h-6 min-w-20 bg-transparent text-sm font-medium outline-none",
                "placeholder:text-muted-foreground/50",
              )}
            />

            {/* Autocomplete dropdown */}
            {suggestions.length > 0 && (
              <div
                role="listbox"
                className="absolute left-0 top-full z-50 mt-1 max-h-48 min-w-40 overflow-auto rounded-lg border border-border bg-popover py-1 shadow-md"
              >
                {suggestions.map((suggestion, idx) => (
                  <button
                    key={suggestion}
                    type="button"
                    role="option"
                    aria-selected={idx === highlightIndex}
                    className={cn(
                      "w-full px-3 py-1.5 text-left text-xs transition-colors",
                      idx === highlightIndex
                        ? "bg-accent text-accent-foreground"
                        : "hover:bg-accent/50",
                    )}
                    onMouseDown={(e) => {
                      e.preventDefault() // prevent blur
                      addTag(suggestion)
                    }}
                    onMouseEnter={() => setHighlightIndex(idx)}
                  >
                    {highlightMatch(suggestion)}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <button
            onClick={startAdding}
            className="inline-flex h-6 items-center rounded-full border border-dashed border-muted-foreground/30 px-2.5 text-xs font-medium text-muted-foreground/50 transition-colors hover:border-muted-foreground/50 hover:text-muted-foreground"
          >
            {t("Add Tag")}
          </button>
        )}
      </div>
    </div>
  )
})
